/**
 * useReleases Hook
 * 
 * Fetches deployment releases from Back4App CLI API.
 * Features:
 * - List releases with cursor-based pagination
 * - Fetch release details (file changes tree)
 * - Fetch file content for viewing
 * - Fetch file diff (current vs previous version)
 * - Track current deployment
 * 
 * Endpoints used:
 * - GET /cli/:id/releases - List releases
 * - GET /cli/:id/releases/:releaseId - Release details
 * - POST /cli/:id/releases/:releaseId/file - File content
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Release,
  ReleasesListResponse,
  ReleaseDetailResponse,
  FileContentResponse,
  FileChangeNode,
} from '../types/AppPanel';
import { BACK4APP_DOT_COM_DASHBOARD_URL } from '../settings';

/** Diff data for a file (current and previous versions) */
export interface FileDiffData {
  currentContent: string;
  previousContent: string | null;
  filename: string;
  changeType: 'added' | 'modified' | 'deleted';
}

interface UseReleasesResult {
  /** List of releases */
  releases: Release[];
  /** Currently active deployment */
  currentDeployment: Release | null;
  /** Loading state for releases list */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Whether there are more releases to load */
  hasMore: boolean;
  /** Load more releases (pagination) */
  loadMore: () => void;
  /** Refetch all releases */
  refetch: () => void;
  
  // Release detail
  /** Currently expanded release ID */
  expandedReleaseId: number | null;
  /** Details of expanded release */
  releaseDetail: ReleaseDetailResponse | null;
  /** Loading state for release detail */
  isLoadingDetail: boolean;
  /** Toggle release expansion */
  toggleRelease: (releaseId: number) => void;
  
  // File content
  /** Fetch file content for viewing */
  fetchFileContent: (
    releaseId: number,
    fileData: FileChangeNode['data']
  ) => Promise<string | null>;
  
  // Diff functionality
  /** Fetch file diff (current and previous versions) */
  fetchFileDiff: (
    releaseId: number,
    node: FileChangeNode
  ) => Promise<FileDiffData | null>;
}

/** Number of releases to fetch per page */
const PAGE_SIZE = 10;

/**
 * Hook to fetch and manage deployment releases
 * 
 * @param appId - Dashboard App ID (UUID format)
 * @param refreshSignal - Optional signal to trigger refetch (incremented externally)
 * @returns Release data and controls
 */
export function useReleases(appId?: string, refreshSignal?: number): UseReleasesResult {
  // Releases list state
  const [releases, setReleases] = useState<Release[]>([]);
  const [currentDeployment, setCurrentDeployment] = useState<Release | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  
  // Release detail state
  const [expandedReleaseId, setExpandedReleaseId] = useState<number | null>(null);
  const [releaseDetail, setReleaseDetail] = useState<ReleaseDetailResponse | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  /**
   * Fetch releases list from API
   * @param cursor - Optional cursor for pagination
   */
  const fetchReleases = useCallback(async (cursor?: string | null) => {
    if (!appId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Build URL with query params
      let url = `${BACK4APP_DOT_COM_DASHBOARD_URL}/cli/${appId}/releases?limit=${PAGE_SIZE}&sort=desc`;
      if (cursor) {
        url += `&cursor=${cursor}`;
      }

      const response = await fetch(url, {
        credentials: 'include',
        headers: { 'Accept': 'application/json' },
      });

      if (!response.ok) {
        if (response.status === 404) {
          // No releases yet - not an error
          setReleases([]);
          setCurrentDeployment(null);
          setHasMore(false);
          return;
        }
        throw new Error(`Failed to fetch releases: ${response.status}`);
      }

      const data: ReleasesListResponse = await response.json();
      
      if (cursor) {
        // Append to existing releases (load more)
        setReleases(prev => [...prev, ...data.data]);
      } else {
        // Replace releases (initial load or refresh)
        setReleases(data.data);
      }
      
      setCurrentDeployment(data.currentDeployment);
      setHasMore(data.pagination.hasMore);
      setNextCursor(data.pagination.nextCursor);
    } catch (err) {
      console.error('Error fetching releases:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch releases');
    } finally {
      setIsLoading(false);
    }
  }, [appId]);

  /**
   * Fetch release detail (file changes tree)
   * @param releaseId - The release ID to fetch details for
   */
  const fetchReleaseDetail = useCallback(async (releaseId: number) => {
    if (!appId) return;

    setIsLoadingDetail(true);

    try {
      const response = await fetch(
        `${BACK4APP_DOT_COM_DASHBOARD_URL}/cli/${appId}/releases/${releaseId}`,
        {
          credentials: 'include',
          headers: { 'Accept': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch release detail: ${response.status}`);
      }

      const data: ReleaseDetailResponse = await response.json();
      setReleaseDetail(data);
    } catch (err) {
      console.error('Error fetching release detail:', err);
      setReleaseDetail(null);
    } finally {
      setIsLoadingDetail(false);
    }
  }, [appId]);

  /**
   * Toggle release card expansion
   * When expanding, fetches release details
   * @param releaseId - The release ID to toggle
   */
  const toggleRelease = useCallback((releaseId: number) => {
    if (expandedReleaseId === releaseId) {
      // Collapse current release
      setExpandedReleaseId(null);
      setReleaseDetail(null);
    } else {
      // Expand and fetch details
      setExpandedReleaseId(releaseId);
      fetchReleaseDetail(releaseId);
    }
  }, [expandedReleaseId, fetchReleaseDetail]);

  /**
   * Fetch file content for a specific release and file
   * @param releaseId - The release ID
   * @param fileData - File metadata from the tree node
   * @returns Decoded file content or null on error
   */
  const fetchFileContent = useCallback(async (
    releaseId: number,
    fileData: FileChangeNode['data']
  ): Promise<string | null> => {
    if (!appId || !fileData) return null;

    try {
      const response = await fetch(
        `${BACK4APP_DOT_COM_DASHBOARD_URL}/cli/${appId}/releases/${releaseId}/file`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            folder: fileData.folder,
            filename: fileData.filename,
            version: fileData.version,
            checksum: fileData.checksum,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }

      const data: FileContentResponse = await response.json();
      
      // Decode base64 content
      try {
        return atob(data.base64);
      } catch {
        // Return as-is if not valid base64
        return data.base64;
      }
    } catch (err) {
      console.error('Error fetching file content:', err);
      return null;
    }
  }, [appId]);

  /**
   * Find a file node in the tree by path (searches recursively)
   */
  const findFileInTree = useCallback((
    nodes: FileChangeNode[],
    folder: string,
    filename: string
  ): FileChangeNode | null => {
    for (const node of nodes) {
      if (node.type === 'folder' && node.children) {
        const found = findFileInTree(node.children, folder, filename);
        if (found) return found;
      } else if (node.data) {
        // Check if this is the file we're looking for
        if (node.data.folder === folder && node.data.filename === filename) {
          return node;
        }
      }
    }
    return null;
  }, []);

  /**
   * Search backwards through releases to find the previous version of a file.
   * This is necessary because a file might not appear in the changes tree
   * of the immediately previous release (N-1) if it wasn't modified there.
   * We need to search backwards until we find a release where the file was
   * either created or modified.
   * 
   * @param startReleaseId - The release to start searching from (usually current - 1)
   * @param folder - The folder of the file
   * @param filename - The filename to search for
   * @returns The file content or null if not found
   */
  const findPreviousFileVersion = useCallback(async (
    startReleaseId: number,
    folder: string,
    filename: string
  ): Promise<string | null> => {
    if (!appId) return null;

    // Search backwards through releases until we find the file
    for (let prevId = startReleaseId; prevId >= 1; prevId--) {
      try {
        const response = await fetch(
          `${BACK4APP_DOT_COM_DASHBOARD_URL}/cli/${appId}/releases/${prevId}`,
          { credentials: 'include', headers: { 'Accept': 'application/json' } }
        );

        if (!response.ok) continue;

        const releaseData: ReleaseDetailResponse = await response.json();
        
        // Look for the file in this release's changes tree
        const fileNode = findFileInTree(releaseData.changes.tree, folder, filename);
        
        if (fileNode?.data) {
          // Found the file! Fetch its content
          const content = await fetchFileContent(prevId, fileNode.data);
          if (content) {
            return content;
          }
        }
      } catch (err) {
        console.error(`Error searching release ${prevId}:`, err);
        // Continue searching older releases
      }
    }

    return null;
  }, [appId, findFileInTree, fetchFileContent]);

  /**
   * Fetch file diff - gets both current and previous versions
   * 
   * For 'modified' files:
   *   - Fetches current version from current release
   *   - Searches backwards through releases to find previous version
   * 
   * For 'added' files:
   *   - Only current version exists (no previous)
   * 
   * For 'deleted' files:
   *   - Only previous version exists (no current)
   *   - Searches backwards to find the last version before deletion
   * 
   * @param releaseId - The current release ID
   * @param node - The file node to fetch diff for
   * @returns FileDiffData with both versions or null on error
   */
  const fetchFileDiff = useCallback(async (
    releaseId: number,
    node: FileChangeNode
  ): Promise<FileDiffData | null> => {
    if (!appId || !node.data) return null;

    const changeType = node.type === 'green-file' ? 'added' 
                     : node.type === 'red-file' ? 'deleted' 
                     : 'modified';

    try {
      // For added files, only fetch current version
      if (changeType === 'added') {
        const currentContent = await fetchFileContent(releaseId, node.data);
        return {
          currentContent: currentContent || '',
          previousContent: null,
          filename: node.data.filename,
          changeType,
        };
      }

      // For deleted files, search backwards to find the last version
      if (changeType === 'deleted') {
        const previousContent = await findPreviousFileVersion(
          releaseId - 1,
          node.data.folder,
          node.data.filename
        );

        return {
          currentContent: '',
          previousContent: previousContent || '// Previous version not available',
          filename: node.data.filename,
          changeType,
        };
      }

      // For modified files, fetch current and search backwards for previous
      const [currentContent, previousContent] = await Promise.all([
        fetchFileContent(releaseId, node.data),
        findPreviousFileVersion(
          releaseId - 1,
          node.data.folder,
          node.data.filename
        ),
      ]);

      return {
        currentContent: currentContent || '',
        previousContent: previousContent || '// Previous version not available',
        filename: node.data.filename,
        changeType,
      };
    } catch (err) {
      console.error('Error fetching file diff:', err);
      return null;
    }
  }, [appId, fetchFileContent, findPreviousFileVersion]);

  /**
   * Load more releases (pagination)
   */
  const loadMore = useCallback(() => {
    if (hasMore && nextCursor && !isLoading) {
      fetchReleases(nextCursor);
    }
  }, [hasMore, nextCursor, isLoading, fetchReleases]);

  /**
   * Refetch all releases from the beginning
   */
  const refetch = useCallback(() => {
    setReleases([]);
    setNextCursor(null);
    setExpandedReleaseId(null);
    setReleaseDetail(null);
    fetchReleases();
  }, [fetchReleases]);

  // Initial fetch when appId changes
  useEffect(() => {
    if (appId) {
      fetchReleases();
    }
  }, [fetchReleases, appId]);

  // Refetch when external refresh signal changes (agent finished or manual refresh)
  useEffect(() => {
    if (refreshSignal && refreshSignal > 0 && appId) {
      // Reset state and refetch from beginning
      setReleases([]);
      setNextCursor(null);
      setHasMore(true);
      setExpandedReleaseId(null);
      setReleaseDetail(null);
      fetchReleases();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  return {
    releases,
    currentDeployment,
    isLoading,
    error,
    hasMore,
    loadMore,
    refetch,
    expandedReleaseId,
    releaseDetail,
    isLoadingDetail,
    toggleRelease,
    fetchFileContent,
    fetchFileDiff,
  };
}

export default useReleases;
